import { supabase } from '../../lib/supabase';
import { StorageService } from './StorageService';
import { AudioSyncService } from './AudioSyncService';
import { DatabaseService } from './DatabaseService';
import { RecordingResult } from './types';
import { FFmpegService } from '../FFmpegService';

/**
 * Handles the processing of recordings
 */
export class RecordingProcessor {
  private storageService = new StorageService();
  private dbService = new DatabaseService();
  
  /**
   * Stop recording and save the result
   *
   * Note: backingTrackUrl should be the record track (vocals-only backing track)
   * which is used during recording. This is the audio_url from the song.
   */
  async stopAndSaveRecording(
    blob: Blob,
    songId: string,
    mode: 'audio' | 'video',
    duration: number,
    backingTrackUrl: string | null,
    tournamentId?: string | null,
    tournamentRound?: number | null
  ): Promise<RecordingResult> {
    try {
      console.log('[SCORING] Blob size:', blob.size, 'type:', blob.type);

      if (blob.size === 0) {
        throw new Error('No audio was recorded. Please check your microphone and try again.');
      }

      // Get session
      console.log('[SCORING] Step 3: Getting session...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Your session has expired. Please log in again to save recordings.');
      }
      console.log('[SCORING] Session OK, user:', session.user.id);

      // For video recordings, convert to MP4 with faststart for proper seeking
      let uploadBlob = blob;
      let fileExtension = 'webm';

      if (mode === 'video') {
        try {
          console.log('[SCORING] Step 4: Converting video to MP4...');
          uploadBlob = await FFmpegService.convertVideoToStreamingMp4(blob);
          fileExtension = 'mp4';
          console.log('[SCORING] Video converted, size:', uploadBlob.size);
        } catch (conversionError) {
          console.error('[SCORING] Video conversion failed, using WebM:', conversionError);
        }
      }

      // Upload the recording
      console.log('[SCORING] Step 5: Uploading recording...');
      const fileName = `${Date.now()}-${mode}-recording.${fileExtension}`;
      const publicUrl = await this.storageService.uploadRecording(
        session.user.id,
        uploadBlob,
        fileName
      );
      console.log('[SCORING] Upload complete:', publicUrl?.substring(0, 80));

      // Get song title for recording title
      console.log('[SCORING] Step 6: Getting song title...');
      const songTitle = await this.dbService.getSongTitle(songId);
      const recordingTitle = `${songTitle} - ${new Date().toLocaleString()}`;

      // Detect audio sync offset if backing track is provided
      let audioSyncOffset: number | null = null;
      if (backingTrackUrl) {
        try {
          console.log('[SCORING] Step 7: Detecting audio sync offset...');
          const backingTrackResponse = await fetch(backingTrackUrl);
          const backingTrackBlob = await backingTrackResponse.blob();

          audioSyncOffset = await AudioSyncService.detectTimingOffset(
            blob,
            backingTrackBlob,
            15 // Max offset of 15 seconds
          );

          console.log('[SCORING] Audio sync offset:', audioSyncOffset.toFixed(3), 'seconds');
        } catch (syncError) {
          console.error('[SCORING] Failed to detect sync offset:', syncError);
          // Continue without offset - not critical for saving
        }
      }

      // Save recording metadata with sync offset
      console.log('[SCORING] Step 8: Creating database record...');
      // Note: vocals_only_url will be populated by the database trigger + edge function
      const savedRecording = await this.dbService.createRecording({
        user_id: session.user.id,
        song_id: songId,
        title: recordingTitle,
        recording_url: publicUrl,
        vocals_only_url: null,
        recording_type: mode,
        duration,
        audio_sync_offset: audioSyncOffset,
        tournament_id: tournamentId,
        tournament_round: tournamentRound
      });

      console.log('[SCORING] Step 9: DB save complete, recording ID:', savedRecording.id);
      console.log('[SCORING] Step 10: Done, returning result');

      // Return the saved recording info
      // Note: vocalsOnlyUrl will be null initially and populated by the backend processing.
      // The mixed/final media is produced server-side by the Jamrock pipeline.
      return {
        url: publicUrl,
        id: savedRecording.id,
        vocalsOnlyUrl: null,
        audioSyncOffset
      };
    } catch (error) {
      console.error('[SCORING] ERROR in stopAndSaveRecording:', error);
      throw error;
    }
  }
}

// Helper function to fetch file data
export async function fetchFile(file: Blob): Promise<Uint8Array> {
  const data = await file.arrayBuffer();
  return new Uint8Array(data);
}